import { useEffect } from "react";
import { RequestValue } from "@/api/options";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import useTabsValidation from "@/hooks/useTabsValidation";
// Core
import AutoDebtPaymentFields from "@/components/AutoDebt";
import CreditCardPaymentFields from "@/components/CreditCard";
// Components
import Layout from "@/components/Layout";

import Button from "@/components/Button";
import Columns from "@/components/Columns";
import Column from "@/components/Column";
import Form from "@/components/Form";
import Text from "@/components/Text";
import Checkbox from "@/components/Checkbox";
import ErrorText from "@/components/ErrorText";
import Anchor from "@/components/Anchor";
import { formatFirstName, removeAccentAndSpace } from "@/utils/string";
import { useActiveStore } from "@/store/activePages";
import ForceChangePaymentMethodAlert from "@/components/ForceChangePaymentMethod";
import { usePaymentMethodChangedStore } from "@/store/paymentMethodChanged";
import Switch from "@/components/Switch";
import SwitchItem from "@/components/SwitchItem";
import ButtonPaymentMethod from "@/components/ButtonPaymentMethod";
import SEO from "@/components/SEO";
import UnauthorizedAlert from "@/components/UnauthorizedAlert";
import {
  usePaymentInfoData,
  Payment,
  payments,
  DEBITO,
  CREDITO,
  FATURA,
  SANITIZED_DEBITO,
  SANITIZED_CREDITO,
  SANITIZED_FATURA,
  barCode,
  creditCard,
} from "./usePaymentInfoData";

// Payment Info Data
export const PaymentInfoData = () => {
  const {
    setKnownClausules,
    setKnownTerms,
    setFormIsValid,
    knownClausules,
    knownTerms,
    formPaymentIsValid,
    data,
    setPaymentTypes,
    paymentTypes,
    validationStepIsOn,
    setNextRoute,
    showForceChangePaymentMethod,
    setShowForceChangePaymentMethod,
    userSaidThatDontWantToChange,
    setUserSaidThatDontWantToChange,
    handleChangePaymentMethod,
    method,
    showUnauthorizedAlert,
    setShowUnauthorizedAlert,
    handleOnSubmit,
    paymentMethods,
    handleClickSetPaymentMethod,
    hasAvailableCorrespondent,
    setFormPaymentIsValid,
    formIsValid,
    sendSMSerror,
    saleError,
    pageConfig,
    isLoading,
    continueButtonText,
    downloadRegulationPlan,
    downloadRegulation,
    paymentDataSchema,
  } = usePaymentInfoData();

  useTabsValidation(pageConfig.route, pageConfig.prevRoute);
  const paymentFormStore = useFormStore((store) => store.value.payment);
  const form = useFormStore((store) => store.value);
  const product = useProductStore((store) => store.item);
  const payment = useProductStore((store) => store.payment);
  const setPayment = useProductStore((store) => store.setPayment);
  const isExpress = useProductStore((store) => store.isExpress);

  const shouldHideCreditCard = usePaymentMethodChangedStore(
    (store) => store.shouldHideCreditCard
  );

  const shouldHideAutomaticDebitAndBilling = usePaymentMethodChangedStore(
    (store) => store.shouldHideAutomaticDebitAndBilling
  );

  const showAutoDebtPayment = usePaymentMethodChangedStore(
    (store) => store.showAutoDebtPayment
  );
  const setShowAutoDebtPayment = usePaymentMethodChangedStore(
    (store) => store.setShowAutoDebtPayment
  );

  const exchangeProhibitedPayment = usePaymentMethodChangedStore(
    (store) => store.exchangeProhibitedPayment
  );

  useEffect(() => {
    setKnownClausules(paymentFormStore?.knownClausules ?? true);
    setKnownTerms(paymentFormStore?.knownTerms ?? true);
  }, [paymentFormStore, setKnownClausules, setKnownTerms]);

  useEffect(() => {
    setFormIsValid(
      knownClausules && knownTerms && formPaymentIsValid && !!payment?.value
    );
  }, [knownClausules, knownTerms, payment, formPaymentIsValid, setFormIsValid]);

  useEffect(() => {
    // So oferece a outra forma de pagamento se o plano correspondente
    // realmente puder ser carregado para o DDD do cliente — `correspondente`
    // preenchido nao basta (pode estar inativo ou sem preco para o DDD).
    const hasCorrespondent = exchangeProhibitedPayment
      ? false
      : hasAvailableCorrespondent;
    const shouldShowCreditCard =
      !shouldHideCreditCard && (isExpress() || hasCorrespondent);
    const shouldShowAutomaticDebitAndBilling =
      !shouldHideAutomaticDebitAndBilling && (!isExpress() || hasCorrespondent);

    const paymentsFiltered =
      data?.paymentTypes?.reduce((result, item) => {
        const key = removeAccentAndSpace(item.value);
        if (payments[key]) {
          if (key === SANITIZED_CREDITO && shouldShowCreditCard) {
            result.push(payments[key]);
          }
          if (key === SANITIZED_DEBITO && shouldShowAutomaticDebitAndBilling) {
            result.push(payments[key]);
          }
          if (key === SANITIZED_FATURA && shouldShowAutomaticDebitAndBilling) {
            result.push(payments[key]);
          }
        }

        return result;
      }, [] as Payment[]) ?? [];

    if (!payment && (form.paymentType || paymentsFiltered[0])) {
      const localPayment = form.paymentType || paymentsFiltered[0];
      setPayment(localPayment as RequestValue);
    }

    setPaymentTypes(paymentsFiltered);
  }, [
    data?.paymentTypes,
    setPayment,
    payment,
    form.paymentType,
    product,
    form.tipo,
    paymentTypes.length,
    shouldHideCreditCard,
    shouldHideAutomaticDebitAndBilling,
    isExpress,
    exchangeProhibitedPayment,
    hasAvailableCorrespondent,
    setPaymentTypes,
  ]);

  const removePage = useActiveStore((store) => store.removePage);

  useEffect(() => {
    let route = pageConfig.nextRoute;

    if (userSaidThatDontWantToChange || !validationStepIsOn) {
      route = "/confirmation/auto-debt";
    }

    setNextRoute(route);
  }, [
    pageConfig.nextRoute,
    setNextRoute,
    validationStepIsOn,
    userSaidThatDontWantToChange,
  ]);

  return (
    <Layout>
      <SEO
        title="Dados de Pagamento"
        description="Adicione os dados de pagamento para o seu novo plano TIM!"
      />
      {showForceChangePaymentMethod && (
        <ForceChangePaymentMethodAlert
          setShowForceChangePaymentMethod={setShowForceChangePaymentMethod}
          setUserSaidThatDontWantToChange={setUserSaidThatDontWantToChange}
          changeMethod={handleChangePaymentMethod}
          method={method}
        />
      )}
      {showUnauthorizedAlert && (
        <UnauthorizedAlert
          setShowUnauthorizedAlert={setShowUnauthorizedAlert}
        />
      )}
      <Column size={12} className="pb-6">
        <Text
          kind="blue"
          size="base"
          className="pt-2 font-bold"
          data-ic-title="sec-omega-comunicacao"
        >
          {form.fullName
            ? `${formatFirstName(form.fullName)}, estamos quase lá!`
            : `Estamos quase lá!`}
          <br />
          Escolha a melhor forma de pagamento para finalizar seu pedido.
        </Text>
      </Column>

      <Columns
        as={Form}
        onSubmit={handleOnSubmit}
        // @ts-expect-error a validação está ok
        validationSchema={paymentDataSchema}
        justifyContent="center"
        alignItems="start"
      >
        <Column size={12} className="pb-6">
          {payment && (
            <>
              <div
                className="lg:flex"
                style={{
                  gap: "1rem",
                }}
              >
                {paymentMethods.map((paymentType) => (
                  <ButtonPaymentMethod
                    key={paymentType.value}
                    label={paymentType.label}
                    value={payment?.value}
                    icon={paymentType.label !== CREDITO ? barCode : creditCard}
                    onClick={() => {
                      handleClickSetPaymentMethod(paymentType.value);
                    }}
                  />
                ))}
              </div>
              {payment?.value !== CREDITO && (
                <Text size="xs" className="py-2 text-tim-primary">
                  Sua fatura, no Email/SMS e no App MEU TIM
                </Text>
              )}
            </>
          )}
        </Column>

        {payment?.value !== CREDITO && (
          <Column size={12}>
            <div className="flex items-center px-2 pb-4">
              <Switch
                name="AutoDebtPayment"
                defaultValue={showAutoDebtPayment}
                type="primary"
                onChange={() => {
                  setShowAutoDebtPayment(!showAutoDebtPayment);
                  if (showAutoDebtPayment) handleClickSetPaymentMethod(FATURA);
                  if (showAutoDebtPayment === false) {
                    handleClickSetPaymentMethod(DEBITO);
                  }
                }}
              >
                <SwitchItem type="primary" value={false} />
                <SwitchItem type="primary" value />
              </Switch>
              <span className="font-medium leading-5 text-tim-primary ">
                Pagar com débito automatico
              </span>
            </div>

            {payment?.value !== CREDITO && (
              <AutoDebtPaymentFields
                hasPayment={payment?.value === DEBITO}
                setFormIsValid={setFormPaymentIsValid}
              />
            )}
          </Column>
        )}

        {payment?.value === CREDITO && (
          <CreditCardPaymentFields setFormIsValid={setFormPaymentIsValid} />
        )}
        {product?.gbPromocional ? (
          <Column size={12} className="pb-4">
            <Checkbox
              name="payment.knownClausules"
              checked={knownClausules}
              onChange={(e) => setKnownClausules(e.target.checked)}
            >
              <Text size="xs" className="leading-tight">
                Declaro que conheço e concordo com o
                <Anchor
                  as="button"
                  onClick={() =>
                    downloadRegulation(
                      "path_regulamento_pacotes_adicionais",
                      "pacotes-adicionais"
                    )
                  }
                  to="#"
                >
                  Regulamento de Pacote Adicional.
                </Anchor>
              </Text>
            </Checkbox>
          </Column>
        ) : null}
        <Column size={12} className="pb-6">
          <Checkbox
            name="payment.knownTerms"
            checked={knownTerms}
            onChange={(e) => setKnownTerms(e.target.checked)}
          >
            <Text size="xs" className="leading-tight">
              Concordo com os
              <Anchor
                as="button"
                onClick={() => downloadRegulationPlan()}
                to="#"
              >
                termos da promoção do {product?.nome}
              </Anchor>
              e conheço a
              <Anchor
                to={product?.sumario_url ?? ''}
              >
                Etiqueta Padrão da Oferta
              </Anchor>
              .
            </Text>
          </Checkbox>
        </Column>

        <Column size={12} className="pb-6 text-center">
          {sendSMSerror || saleError ? (
            <ErrorText id="process-error">
              Ocorreu um erro ao finalizar o processo. Por favor, tente
              novamente.
            </ErrorText>
          ) : null}
        </Column>

        <Column size={12}>
          <Button
            type="submit"
            large
            disabled={!formIsValid}
            helperText={!formIsValid ? "todos os campos são obrigatórios" : ""}
            loading={isLoading}
          >
            {!formIsValid
              ? "Preencha os campos para continuar"
              : continueButtonText()}
          </Button>
        </Column>
        <Column size={12} className="mt-8">
          <Button
            type="button"
            kind="secondary"
            large
            onClick={() => removePage(pageConfig.route)}
            to={pageConfig.prevRoute}
            data-ic-trigger="sec-omega-voltar"
          >
            Voltar
          </Button>
        </Column>
      </Columns>
    </Layout>
  );
};

export default PaymentInfoData;
